/**
 * Search Drawer - Mobile Search Web Component
 * Slide-in drawer for search, matches mobile navigation pattern
 */

class SearchDrawer extends HTMLElement {
  constructor() {
    super();

    this.toggle = this.querySelector('.search-drawer__toggle');
    this.panel = this.querySelector('.search-drawer__panel');
    this.overlay = this.querySelector('.search-drawer__overlay');
    this.closeBtn = this.querySelector('.search-drawer__close');
    this.input = this.querySelector('.search-drawer__input');
    this.resetBtn = this.querySelector('.search-drawer__reset');
    this.form = this.querySelector('.search-drawer__form');

    this.isOpen = false;
    this.previousFocus = null;
    this.scrollPosition = 0;

    this.handleToggleClick = this.handleToggleClick.bind(this);
    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
  }

  connectedCallback() {
    this.bindEvents();
  }

  disconnectedCallback() {
    this.unbindEvents();
  }

  bindEvents() {
    this.toggle?.addEventListener('click', this.handleToggleClick);
    this.overlay?.addEventListener('click', this.handleOverlayClick);
    this.closeBtn?.addEventListener('click', this.handleCloseClick);
    this.input?.addEventListener('input', this.handleInputChange);
    this.resetBtn?.addEventListener('click', this.handleReset);
    this.form?.addEventListener('submit', this.handleFormSubmit);
    document.addEventListener('keydown', this.handleKeyDown);
  }

  unbindEvents() {
    this.toggle?.removeEventListener('click', this.handleToggleClick);
    this.overlay?.removeEventListener('click', this.handleOverlayClick);
    this.closeBtn?.removeEventListener('click', this.handleCloseClick);
    this.input?.removeEventListener('input', this.handleInputChange);
    this.resetBtn?.removeEventListener('click', this.handleReset);
    this.form?.removeEventListener('submit', this.handleFormSubmit);
    document.removeEventListener('keydown', this.handleKeyDown);
  }

  open() {
    if (this.isOpen) return;

    // Close mobile drawer if open
    const mobileDrawer = document.querySelector('mobile-drawer');
    if (mobileDrawer && mobileDrawer.isOpen) {
      mobileDrawer.close();
    }

    this.isOpen = true;
    this.previousFocus = document.activeElement;
    this.scrollPosition = window.scrollY;

    this.classList.add('search-drawer--open');
    document.body.classList.add('search-drawer-open');
    document.body.style.top = `-${this.scrollPosition}px`;

    this.toggle?.setAttribute('aria-expanded', 'true');
    this.panel?.setAttribute('aria-hidden', 'false');

    // Focus input after transition
    setTimeout(() => {
      this.input?.focus();
    }, 100);

    // Trap focus within panel
    this.trapFocus();

    this.dispatchEvent(new CustomEvent('search-drawer:open', { bubbles: true }));
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;

    this.classList.remove('search-drawer--open');
    document.body.classList.remove('search-drawer-open');
    document.body.style.top = '';
    window.scrollTo(0, this.scrollPosition);

    this.toggle?.setAttribute('aria-expanded', 'false');
    this.panel?.setAttribute('aria-hidden', 'true');

    // Release focus trap
    this.releaseFocusTrap();

    // Restore focus to toggle
    if (this.previousFocus?.focus) {
      this.previousFocus.focus();
    }

    this.dispatchEvent(new CustomEvent('search-drawer:close', { bubbles: true }));
  }

  handleToggleClick(event) {
    event.preventDefault();
    this.isOpen ? this.close() : this.open();
  }

  handleOverlayClick(event) {
    event.preventDefault();
    this.close();
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.close();
  }

  handleKeyDown(event) {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }

    // Tab trapping
    if (event.key === 'Tab') {
      this.handleTabKey(event);
    }
  }

  handleInputChange() {
    if (this.input && this.resetBtn) {
      if (this.input.value.length > 0) {
        this.resetBtn.classList.remove('hidden');
      } else {
        this.resetBtn.classList.add('hidden');
      }
    }
  }

  handleReset(event) {
    event.preventDefault();
    if (this.input) {
      this.input.value = '';
      this.input.focus();

      // Trigger input event for predictive search to clear
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this.resetBtn?.classList.add('hidden');
  }

  handleFormSubmit(event) {
    // Allow form to submit if there's a search term
    if (this.input && this.input.value.trim() === '') {
      event.preventDefault();
      this.input.focus();
    }
  }

  // Focus management
  getFocusableElements() {
    if (!this.panel) return [];

    const focusableSelectors = [
      'button:not([disabled])',
      'a[href]',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(', ');

    return Array.from(this.panel.querySelectorAll(focusableSelectors));
  }

  trapFocus() {
    this.focusableElements = this.getFocusableElements();
    this.firstFocusable = this.focusableElements[0];
    this.lastFocusable = this.focusableElements[this.focusableElements.length - 1];
  }

  releaseFocusTrap() {
    this.focusableElements = null;
    this.firstFocusable = null;
    this.lastFocusable = null;
  }

  handleTabKey(event) {
    if (!this.focusableElements || this.focusableElements.length === 0) return;

    const activeElement = document.activeElement;

    if (event.shiftKey) {
      // Shift + Tab: going backwards
      if (activeElement === this.firstFocusable) {
        event.preventDefault();
        this.lastFocusable?.focus();
      }
    } else {
      // Tab: going forwards
      if (activeElement === this.lastFocusable) {
        event.preventDefault();
        this.firstFocusable?.focus();
      }
    }
  }
}

// Register custom element
if (!customElements.get('search-drawer')) {
  customElements.define('search-drawer', SearchDrawer);
}
